import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  type AutocompleteInputChangeReason,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { User } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  UserFilterFragment,
  Users_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  filterPopperProps,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useLocale } from '@shared/hooks/locale';

export const USER_FILTER_FRAGMENT = gql(/* GraphQL */ `
  fragment UserFilter on users {
    ...UserAvatar
    id
    full_name
    primaryEmail: emails(
      order_by: [{ primary: desc }, { verified: desc }]
      limit: 1
    ) {
      email
    }
  }
`);

const SEARCH_USERS_TSV = gql(/* GraphQL */ `
  query UserFilterSearchUsersTsv($q: String!, $where: users_bool_exp) {
    search_users_tsv(
      args: { search_text: $q }
      where: $where
      order_by: [{ is_broker: desc }, { full_name: asc }]
      limit: 10
    ) {
      id
      ...UserFilter
    }
  }
`);

const GET_DEFAULT_USERS = gql(/* GraphQL */ `
  query GetUsersByIds(
    $ids: [uuid!]!
    $isNoneSelected: Boolean!
    $where: users_bool_exp
  ) {
    selected_users: users(where: { id: { _in: $ids } })
      @skip(if: $isNoneSelected) {
      id
      ...UserFilter
    }

    initial_users: users(
      where: $where
      order_by: [{ is_broker: desc }, { full_name: asc }]
      limit: 10
    ) {
      id
      ...UserFilter
    }
  }
`);

type NullableUserOption = { id: null; full_name: string };
type UserOption = UserFilterFragment | NullableUserOption;

type UserSelectBaseProps = {
  onClose?: () => void;
  isNullable: boolean;
  filters?: Users_Bool_Exp;
};

type UserMultiSelectMultipleProps = UserSelectBaseProps & {
  multiple?: true;
  onChange: (value: (string | null)[]) => void;
  defaultValue: (string | null)[];
};

type UserSingleSelectProps = UserSelectBaseProps & {
  multiple: false;
  onChange: (value: string | null) => void;
  defaultValue: string | null;
};

type UserSelectProps = UserMultiSelectMultipleProps | UserSingleSelectProps;

export const UserMultiSelect = (props: UserSelectProps) => {
  const { onClose, isNullable, filters } = props;
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();
  const [options, setOptions] = useState<UserFilterFragment[]>([]);
  const [inputValue, setInputValue] = useState('');

  const isNoneSelected =
    props.multiple === false
      ? props.defaultValue == null
      : props.defaultValue.includes(null);

  const noUserOption = useMemo(
    () => ({
      id: null,
      full_name: t('None'),
    }),
    [t],
  );

  const [selectedOptions, setSelectedOptions] = useState<UserOption[]>(
    isNoneSelected && isNullable ? [noUserOption] : [],
  );

  const [searchUsers, { loading: searchLoading }] = useLazyQuery(
    SEARCH_USERS_TSV,
    { notifyOnNetworkStatusChange: true },
  );

  const defaultIds = useMemo((): string[] => {
    if (props.multiple === false) {
      return props.defaultValue != null ? [props.defaultValue] : [];
    }
    return props.defaultValue.filter(v => v != null);
  }, [props.multiple, props.defaultValue]);

  const { data, loading: defaultLoading } = useQuery(GET_DEFAULT_USERS, {
    variables: {
      ids: defaultIds,
      isNoneSelected,
      where: { id: { _nin: defaultIds }, ...(filters ?? {}) },
    },
  });

  const updateOptions = useMemo(
    () =>
      (newValue: UserFilterFragment[], initialTeams?: UserFilterFragment[]) => {
        const combined = [...newValue, ...(initialTeams ?? [])];
        const uniqueCombined = combined.filter(
          (team, idx, arr) => arr.findIndex(t => t.id === team.id) === idx,
        );
        setOptions(uniqueCombined);
      },
    [],
  );

  useEffect(() => {
    if (data && options.length === 0 && !inputValue) {
      setSelectedOptions(
        isNoneSelected && isNullable
          ? [noUserOption]
          : data?.selected_users ?? [],
      );
      updateOptions(data?.selected_users ?? [], data?.initial_users ?? []);
    }
  }, [
    data,
    inputValue,
    isNoneSelected,
    isNullable,
    noUserOption,
    options.length,
    updateOptions,
  ]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        const { data: searchData } = await searchUsers({
          variables: {
            q: searchValue,
            where: {
              id: { _nin: defaultIds },
              ...(filters ?? {}),
            },
          },
        });
        if (!searchData?.search_users_tsv?.length) {
          setOptions([]);
        } else {
          setOptions(searchData?.search_users_tsv ?? []);
        }
      }, 275),
    [searchUsers, defaultIds, filters],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue) {
        debouncedSearch(searchValue);
      } else {
        updateOptions(data?.selected_users ?? [], data?.initial_users);
      }
    },
    [data?.initial_users, data?.selected_users, debouncedSearch, updateOptions],
  );

  const displayOptions = useMemo(() => {
    if (!isNullable || options.length === 0) {
      return options;
    }
    return [noUserOption, ...options];
  }, [isNullable, noUserOption, options]);

  const handleToggleOptions = useCallback(
    (_: any, newValue: UserOption[] | UserOption | null) => {
      const newArray =
        newValue != null
          ? Array.isArray(newValue)
            ? newValue
            : [newValue]
          : [];

      if (props.multiple === false) {
        setSelectedOptions(newArray);
        props.onChange(newArray.length > 0 ? newArray[0].id : null);
      } else {
        const currentHasNull = selectedOptions.some(v => v.id == null);
        const newHasBroker = newArray.some(v => v.id != null);

        const finalValue =
          currentHasNull && newHasBroker
            ? newArray.filter(v => v.id != null)
            : newArray.some(v => v.id == null)
            ? [noUserOption]
            : newArray;

        setSelectedOptions(finalValue);
        props.onChange(finalValue.map(v => v.id));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange, noUserOption, selectedOptions],
  );

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason !== 'input') {
        return;
      }
      setInputValue(newValue);
      handleSearch(newValue);
    },
    [handleSearch],
  );

  const loading = searchLoading || defaultLoading;

  return (
    <Autocomplete
      multiple={props.multiple !== false}
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      disableClearable={props.multiple !== false}
      size="small"
      clearOnBlur={false}
      {...filterPopperProps}
      value={
        props.multiple !== false ? selectedOptions : selectedOptions[0] ?? null
      }
      loading={loading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={displayOptions}
      getOptionLabel={option => option.full_name ?? ''}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={handleToggleOptions}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
          }}
        />
      )}
      renderOption={(optionProps, option, { selected }) => (
        <ListItem {...optionProps} key={option.id} dense sx={{ py: 0.25 }}>
          {props.multiple !== false && (
            <ListItemIcon sx={{ minWidth: 0, mr: 0.5 }}>
              <Checkbox
                size="small"
                edge="start"
                checked={selected}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
          )}
          {option.id != null && (
            <ListItemAvatar sx={{ minWidth: 0, mr: 1 }}>
              <UserAvatar user={option} size={34} />
            </ListItemAvatar>
          )}
          <ListItemText
            primary={option.full_name}
            secondary={option.id != null ? option.primaryEmail[0]?.email : null}
            slotProps={{
              secondary: { sx: { fontSize: '0.75rem' }, noWrap: true },
            }}
          />
        </ListItem>
      )}
      noOptionsText={t('No options')}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export const UserFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
  isNullable,
  filter,
  onChange,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();

  const nullValuePath = useMemo(() => {
    const [nullPath, ...restPath] = path;
    return [nullPath, '_not', ...restPath];
  }, [path]);

  const defaultValue = useMemo(
    () =>
      getValueFromPath(path, where)?.id?._in ??
      (getValueFromPath(nullValuePath, where) ? [null] : []),
    [getValueFromPath, path, where, nullValuePath],
  );

  // Filters the autocomplete list of users
  const whereFilter = filter
    ? addWhereClause({}, filter.path, filter.value)
    : {};

  const handleChange = useCallback(
    (value: (string | null)[]) => {
      let newWhere = deleteWhereClause(where, path, true);

      if (defaultValue.includes(null)) {
        newWhere = deleteWhereClause(newWhere, nullValuePath, true);
      }

      if (value.length > 0 && value.includes(null)) {
        newWhere = addWhereClause(newWhere, nullValuePath, {});
      } else if (value.length > 0) {
        newWhere = addWhereClause(newWhere, [...path, 'id', '_in'], value);
      }
      newWhere = onChange?.(value, newWhere) ?? newWhere;
      setFiltersParams(newWhere);
    },
    [
      deleteWhereClause,
      where,
      path,
      defaultValue,
      setFiltersParams,
      nullValuePath,
      addWhereClause,
      onChange,
    ],
  );

  const handleDelete =
    defaultValue.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<User size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <UserMultiSelect
          isNullable={isNullable ?? false}
          onClose={handleClose}
          onChange={handleChange}
          defaultValue={defaultValue}
          filters={whereFilter}
        />
      )}
    />
  );
};
